import { useState, useEffect } from 'react';
import { isControlledComponent, getEffectiveExpandedState } from '../utils/expandableUtils';

/**
 * Custom hook for managing expandable section state
 * Handles both controlled and uncontrolled modes
 */
export const useExpandableState = ({
  initiallyExpanded = false,
  expanded: controlledExpanded,
  animateExpand = false,
  onExpand,
}) => {
  // Internal state for uncontrolled mode
  const [internalExpanded, setInternalExpanded] = useState(() => 
    !animateExpand && initiallyExpanded
  );

  // Determine if component is controlled
  const isControlled = isControlledComponent(controlledExpanded);
  
  // Get effective expanded state
  const expanded = getEffectiveExpandedState(controlledExpanded, internalExpanded);

  // Handle initial expansion for animateExpand
  useEffect(() => {
    if (animateExpand) {
      const timer = setTimeout(() => {
        if (onExpand) {
          onExpand(!internalExpanded);
        }
        setInternalExpanded(true);
      }, 100);
      return () => clearTimeout(timer);
    } else if (initiallyExpanded && !isControlled) {
      setInternalExpanded(true);
    }
  }, [animateExpand, initiallyExpanded, onExpand, internalExpanded, isControlled]);

  // Handle prop changes for initiallyExpanded
  useEffect(() => {
    if (!animateExpand && initiallyExpanded && !isControlled) {
      setInternalExpanded(initiallyExpanded);
    }
  }, [initiallyExpanded, animateExpand, isControlled]);

  // Toggle function for internal state
  const toggleExpanded = (newExpandedState) => {
    if (onExpand) {
      onExpand(newExpandedState);
    }
    
    // Only update internal state if not controlled
    if (!isControlled) {
      setInternalExpanded(newExpandedState);
    }
  };

  return {
    expanded,
    isControlled,
    toggleExpanded,
  };
};



// WEBPACK FOOTER //
// ./src/components/ExpandableSection/hooks/useExpandableState.js